import {
  TableCell,
  TableRow,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState, useEffect } from "react";
import { updateTherapistManagement } from "../../api/therapistNomination";

const RowTherapist = ({ row, onSaveSuccess, selectedMonth }) => {
  const [localRow, setLocalRow] = useState(row);
  const [isUpdating, setIsUpdating] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const mainNominationPreview =
    Number(localRow.main_nomination_therapist_amount) || 0;
    
  useEffect(() => {
    setLocalRow(row);
  }, [row]);

  const handleChange = (e, nominationId) => {
    const { name, value } = e.target;
    setLocalRow((prev) => ({
      ...prev,
      nominations: prev.nominations.map((nom) => {
        if (nom.nomination_id === nominationId) {
          return {
            ...nom,
            [name]: value,
          };
        }
        return nom;
      }),
    }));
  };

  const handleToggle = (e, nominationId) => {
    const checked = e.target.checked;
    setLocalRow((prev) => ({
      ...prev,
      nominations: prev.nominations.map((nom) => {
        if (nom.nomination_id === nominationId) {
          if (checked) {
            const restored =
              nom.therapist_amount !== null && nom.therapist_amount !== undefined
                ? nom.therapist_amount
                : nom.therapist_amount_backup ?? "";
            return {
              ...nom,
              therapist_amount: restored,
            };
          } else {
            return {
              ...nom,
              therapist_amount_backup:
                nom.therapist_amount ?? nom.therapist_amount_backup ?? "",
              therapist_amount: null,
            };
          }
        }
        return nom;
      }),
    }));
  };

  const handleSave = async () => {
    try {
      setIsUpdating(true);
      const therapist_nominations = localRow.nominations.map((nom) => ({
        nomination_id: nom.nomination_id,
        therapist_amount:
          nom.therapist_amount === "" ? null : nom.therapist_amount,
        nomination_cost: nom.nomination_cost,
      }));

      const yearMonth = selectedMonth
        ? `${selectedMonth.getFullYear()}-${String(
            selectedMonth.getMonth() + 1
          ).padStart(2, "0")}`
        : null;

      await updateTherapistManagement(localRow.id, {
        therapist_nominations,
        year_month: yearMonth,
      });

      setOpenSuccessDialog(true);
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error("Error saving therapist nominations:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
  };

  const renderNominationCell = (nomination) => {
    if (!nomination) return null;
    const therapistAmount = Number(nomination.therapist_amount) || 0;
    const nominationCost = Number(nomination.nomination_cost) || 0;
    const isMainNomination = nomination.name === "本指名";

    const isSwitchOn = nomination.therapist_amount !== null;
    const displayMainAmount = isSwitchOn ? therapistAmount : mainNominationPreview;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TextField
            size="small"
            label="お客様へ請求する金額"
            name="nomination_cost"
            value={nominationCost}
            onChange={(e) => handleChange(e, nomination.nomination_id)}
            sx={{
              width: "184px",
              marginTop: "10px",
              "& .MuiInputLabel-root": {
                fontSize: "10px",
                top: "5px",
              },
              "& .MuiInputBase-input": {
                fontSize: "0.875rem",
                padding: "4px 8px",
              },
            }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={isSwitchOn}
                onChange={(e) => handleToggle(e, nomination.nomination_id)}
                sx={{
                  transform: "rotate(90deg)",
                  "& .MuiSwitch-thumb": {
                    transform: "rotate(-90deg)",
                  },
                }}
                size="small"
              />
            }
            label=""
          />
          
        </Box>
        
        {isMainNomination && (
          <Box
            sx={{
              fontSize: "10px",
              color: "text.secondary",
              textAlign: "left",
              fontWeight: 600,
            }}
          >
            現在の本指名はバック： {displayMainAmount}
          </Box>
        )}
        {isSwitchOn && (
          <TextField
            size="small"
            label="セラピストの給料"
            name="therapist_amount"
            value={therapistAmount}
            onChange={(e) => handleChange(e, nomination.nomination_id)}
            sx={{
              width: "184px",
              marginTop: "10px",
              "& .MuiInputLabel-root": {
                fontSize: "10px",
                top: "5px",
              },
              "& .MuiInputBase-input": {
                fontSize: "0.875rem",
                padding: "4px 8px",
              },
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <>
      <TableRow>
        <TableCell align="center" sx={{ fontSize: "0.875rem", padding: "8px" }}>
          {localRow.id}
        </TableCell>
        <TableCell
          align="center"
          sx={{ fontSize: "0.875rem", padding: "8px", minWidth: "140px" }}
        >
          {localRow.name || ""}
        </TableCell>
        {localRow.nominations.map((nomination) => (
          <TableCell
            key={nomination.nomination_id}
            align="center"
            sx={{ padding: "8px" }}
          >
            {renderNominationCell(nomination)}
          </TableCell>
        ))}
        <TableCell align="center" sx={{ padding: "8px" }}>
          <Button
            variant="outlined"
            color="success"
            onClick={handleSave}
            disabled={isUpdating}
            startIcon={
              isUpdating ? <CircularProgress size={16} color="success" /> : null
            }
            size="small"
            sx={{ fontSize: "0.75rem" }}
          >
            {isUpdating ? "更新中..." : "登録"}
          </Button>
        </TableCell>
      </TableRow>

      <Dialog
        open={openSuccessDialog}
        onClose={handleCloseSuccessDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>
          セラピスト指名料
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>指名料を確定しました</Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseSuccessDialog}
            color="inherit"
            size="small"
            sx={{
              color: "#666666",
              "&:hover": {
                backgroundColor: "rgba(102, 102, 102, 0.04)",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RowTherapist;
